/**
 * VhalGrpcServiceProvider
 *
 * Mounts the `VhalService` for a single emulator. The service talks to
 * the AOSP VehicleServer through ctrl's gRPC-Web pass-through.
 *
 * On mount, the provider:
 *  1. Calls CheckHealth to flip `isAvailable`.
 *  2. Reads the full property config catalog via `getAllPropertyConfig`
 *     server-streaming.
 *
 * Subscriptions are implemented as polling for v1 — a true streaming
 * `Subscribe` + `startPropertyValuesStream` wire-up is a follow-up.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode,
} from "react";
import {
  VehiclePropertyAccess as AospAccess,
  type VehiclePropConfig,
  type VehiclePropValue,
} from "@repo/sdk/vhal";
import {
  getPropertyInfo,
  getPropertyType,
  VhalPropertyAccess,
  VhalPropertyChangeMode,
  VhalPropertyType,
  type VhalAreaConfig,
  type VhalPropertyConfig,
  type VhalPropertyValue,
} from "../types/vhal";
import type {
  ParsedPropertyValue,
  VhalError,
  VhalService,
} from "../types/vhalService";
import { ConnectError, Code } from "@connectrpc/connect";
import { useVhalGrpcClient } from "./VhalGrpcProvider";
import { staticCatalogConfigs } from "./staticCatalog";
import { useCuttlefishAdb } from "../hooks/useCuttlefishAdb";
import { runShell } from "../lib/adbShell";
import {
  parseHalPropValue,
  formatSetData as adbFormatSetData,
} from "../lib/vhalAdbParser";

// ─── Type conversion ──────────────────────────────────────────────────────

function aospAccessToLocal(a: AospAccess): VhalPropertyAccess {
  // Enum values match by intent (NONE=0/READ=1/WRITE=2/READ_WRITE=3).
  return a as unknown as VhalPropertyAccess;
}

function configFromAosp(c: VehiclePropConfig): VhalPropertyConfig {
  return {
    propertyId: c.prop,
    access: aospAccessToLocal(c.access),
    changeMode: c.changeMode as unknown as VhalPropertyChangeMode,
    areaConfigs: c.areaConfigs?.map((a) => {
      // Only emit int64 range fields when they carry a real value.
      // The proto default is 0n; emitting bigint(0) here would later break
      // the inspector's JSON.stringify of the config catalog (bigints are
      // not JSON-serializable). The ADB parser matched this behavior — it
      // never set min/max int64 unless they appeared in the source text.
      const out: VhalAreaConfig = {
        areaId: a.areaId,
        minInt32Value: a.minInt32Value,
        maxInt32Value: a.maxInt32Value,
        minFloatValue: a.minFloatValue,
        maxFloatValue: a.maxFloatValue,
        // AOSP exposes supported enum values as int64; cast back to number
        // for the UI which assumes 32-bit enums.
        supportedEnumValues: a.supportedEnumValues?.map((v) => Number(v)),
      };
      if (a.minInt64Value !== 0n) out.minInt64Value = a.minInt64Value;
      if (a.maxInt64Value !== 0n) out.maxInt64Value = a.maxInt64Value;
      return out;
    }),
    configArray: c.configArray,
    configString:
      c.configString && c.configString.length > 0
        ? new TextDecoder().decode(c.configString)
        : undefined,
    minSampleRate: c.minSampleRate,
    maxSampleRate: c.maxSampleRate,
    propertyType: getPropertyType(c.prop),
  };
}

function parsedFromAosp(v: VehiclePropValue): ParsedPropertyValue {
  const out: ParsedPropertyValue = {
    propertyId: v.prop,
    areaId: v.areaId,
    status: v.status,
    timestamp: Number(v.timestamp),
  };
  if (v.int32Values?.length) out.int32Values = [...v.int32Values];
  if (v.int64Values?.length) out.int64Values = [...v.int64Values];
  if (v.floatValues?.length) out.floatValues = [...v.floatValues];
  if (v.stringValue) out.stringValue = v.stringValue;
  if (v.byteValues?.length) out.byteValues = v.byteValues;
  return out;
}

function valueFromAosp(v: VehiclePropValue): VhalPropertyValue {
  return parsedFromAosp(v) as VhalPropertyValue;
}

let nextRequestId = 1n;
function reqId(): bigint {
  const id = nextRequestId;
  nextRequestId += 1n;
  return id;
}

// ─── Context ──────────────────────────────────────────────────────────────

/** Exported so dev tooling (MSW mock mode) can supply an in-memory service. */
export const VhalServiceContext = createContext<VhalService | null>(null);

export function useCuttlefishVhalContext(): VhalService {
  const ctx = useContext(VhalServiceContext);
  if (!ctx) {
    throw new Error(
      "useCuttlefishVhalContext must be used within VhalGrpcServiceProvider",
    );
  }
  return ctx;
}

// ─── Provider ─────────────────────────────────────────────────────────────

export interface VhalGrpcServiceProviderProps {
  /** Active emulator ID. When undefined, the service is unavailable. */
  emulatorId?: string;
  children: ReactNode;
}

export const VhalGrpcServiceProvider: FC<VhalGrpcServiceProviderProps> = ({
  emulatorId,
  children,
}) => {
  if (!emulatorId) {
    return (
      <VhalServiceContext.Provider value={unavailableService}>
        {children}
      </VhalServiceContext.Provider>
    );
  }
  return (
    <ActiveProvider emulatorId={emulatorId}>{children}</ActiveProvider>
  );
};

// GEAR_SELECTION (0x11400400 = 289408000) — present on every AAOS build.
const GEAR_SELECTION_PROP = 289408000;

const ActiveProvider: FC<{ emulatorId: string; children: ReactNode }> = ({
  emulatorId,
  children,
}) => {
  const client = useVhalGrpcClient(emulatorId);
  const { executeShell } = useCuttlefishAdb();

  // "grpc" until a CheckHealth Unavailable error flips it to "adb".
  const transportRef = useRef<"grpc" | "adb">("grpc");
  // inject-vhal-event (used for ADB writes to READ-only props) doesn't
  // persist into the HAL store, so reads are overlaid with the last
  // injected value. Keyed like ParsedPropertyValue: `${propertyId}_${areaId}`.
  const shadowValues = useRef<Map<string, ParsedPropertyValue>>(new Map());

  const [isAvailable, setIsAvailable] = useState(false);
  const [discoveredProperties, setDiscoveredProperties] = useState<
    VhalPropertyConfig[] | null
  >(null);
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [lastError, setLastError] = useState<VhalError | null>(null);
  const [transport, setTransport] = useState<"grpc" | "adb">("grpc");
  const [catalogSource, setCatalogSource] = useState<"device" | "static" | null>(null);
  const [attempt, setAttempt] = useState(0);

  const clearError = useCallback(() => setLastError(null), []);

  const recordError = useCallback(
    (operation: string, e: unknown, propertyId?: number) => {
      const message = e instanceof Error ? e.message : String(e);
      setLastError({
        message,
        operation,
        command: propertyId != null
          ? `0x${propertyId.toString(16)}`
          : undefined,
        timestamp: Date.now(),
      });
      console.error(`[VHAL/gRPC] ${operation}:`, message);
    },
    [],
  );

  // Active AbortController for the streaming config fetch so a teardown
  // (component unmount / emulator switch) actually closes the underlying
  // fetch. Without this, an in-flight server-stream keeps reading messages
  // and may try to setState after unmount.
  const discoverAbortRef = useRef<AbortController | null>(null);

  // ── discoverProperties ────────────────────────────────────────────
  const discoverProperties = useCallback(async (): Promise<
    VhalPropertyConfig[]
  > => {
    setIsDiscovering(true);
    clearError();
    discoverAbortRef.current?.abort();
    const ac = new AbortController();
    discoverAbortRef.current = ac;
    try {
      const out: VhalPropertyConfig[] = [];
      for await (const cfg of client.getAllPropertyConfig(
        {},
        { signal: ac.signal },
      )) {
        out.push(configFromAosp(cfg));
      }
      if (ac.signal.aborted) return [];
      // Old emulators (no vhal_proxy_server) can't serve getAllPropertyConfig
      // over the ADB fallback, so live discovery yields nothing. Fall back to
      // the bundled static catalog so the Controls panel still renders; live
      // values continue to flow through getValues/setValues.
      const configs = out.length > 0 ? out : staticCatalogConfigs();
      setCatalogSource(out.length > 0 ? "device" : "static");
      setDiscoveredProperties(configs);
      return configs;
    } catch (e) {
      if (ac.signal.aborted) return [];
      // Discovery threw (typically gRPC Unavailable on old emulators). The
      // panel is still usable from the static catalog, so don't surface a
      // scary error banner — just log for observability.
      console.warn(
        "[VHAL/gRPC] discoverProperties: falling back to static catalog:",
        e instanceof Error ? e.message : e,
      );
      const configs = staticCatalogConfigs();
      setCatalogSource("static");
      setDiscoveredProperties(configs);
      return configs;
    } finally {
      if (discoverAbortRef.current === ac) discoverAbortRef.current = null;
      setIsDiscovering(false);
    }
  }, [client, clearError, recordError]);

  // ── checkHealth + initial discovery ───────────────────────────────
  const initDone = useRef(false);
  useEffect(() => {
    if (initDone.current) return;
    initDone.current = true;

    let cancelled = false;
    (async () => {
      try {
        await client.checkHealth({});
        if (cancelled) return;
        setIsAvailable(true);
        await discoverProperties();
      } catch (e) {
        if (cancelled) return;
        const isUnavailable =
          e instanceof ConnectError && e.code === Code.Unavailable;
        if (isUnavailable) {
          // Old emulator: no vhal_proxy_server. Switch to ADB-in-browser transport.
          console.warn(
            "[VHAL] gRPC Unavailable — falling back to ADB-in-browser transport",
          );
          transportRef.current = "adb";
          setTransport("adb");
          // Health-check via ADB: GEAR_SELECTION is present on every AAOS build.
          try {
            const out = await runShell(
              executeShell,
              `cmd car_service get-property-value ${GEAR_SELECTION_PROP} 0`,
            );
            if (!cancelled && out.includes("HalPropValue{")) {
              setIsAvailable(true);
              await discoverProperties();
            } else if (!cancelled) {
              setIsAvailable(false);
              recordError("checkHealth", "ADB car_service probe returned unexpected output");
            }
          } catch (adbErr) {
            if (!cancelled) {
              setIsAvailable(false);
              recordError("checkHealth", adbErr);
            }
          }
        } else {
          setIsAvailable(false);
          recordError("checkHealth", e);
        }
      }
    })();
    return () => {
      cancelled = true;
      discoverAbortRef.current?.abort();
    };
  }, [client, discoverProperties, executeShell, recordError, attempt]);

  // Re-run the health check + discovery (reconnects the stream, never restarts the emulator).
  const retry = useCallback(() => {
    initDone.current = false;
    transportRef.current = "grpc";
    setTransport("grpc");
    setAttempt((a) => a + 1);
  }, []);

  // Reset on emulator change (component re-mounts because the parent keys
  // the active provider by emulator ID, but be defensive).
  useEffect(() => {
    initDone.current = false;
    transportRef.current = "grpc";
    shadowValues.current.clear();
    setIsAvailable(false);
    setDiscoveredProperties(null);
  }, [emulatorId]);

  // ── listProperties / getPropertyConfig ────────────────────────────
  const listProperties = useCallback(async (): Promise<
    VhalPropertyConfig[]
  > => {
    if (discoveredProperties) return discoveredProperties;
    return discoverProperties();
  }, [discoveredProperties, discoverProperties]);

  const getPropertyConfig = useCallback(
    (propertyId: number): VhalPropertyConfig | null =>
      discoveredProperties?.find((p) => p.propertyId === propertyId) ?? null,
    [discoveredProperties],
  );

  // ── getProperty ──────────────────────────────────────────────────
  const getProperty = useCallback(
    async (
      propertyId: number,
      areaId = 0,
    ): Promise<VhalPropertyValue | null> => {
      if (transportRef.current === "adb") {
        const shadowKey = `${propertyId}_${areaId}`;
        // The shadow (from a prior inject-vhal-event write) is authoritative
        // for injected read-only properties — check it before spending a
        // concurrency slot and a full ADB round-trip on a read that would
        // just be overridden by the shadow anyway.
        const existingShadow = shadowValues.current.get(shadowKey);
        if (existingShadow) return existingShadow as VhalPropertyValue;
        try {
          const out = await runShell(
            executeShell,
            `cmd car_service get-property-value ${propertyId} ${areaId}`,
          );
          const parsed = parseHalPropValue(out, propertyId, areaId);
          if (!parsed) return null;
          return parsed as VhalPropertyValue;
        } catch (e) {
          recordError("getProperty", e, propertyId);
          return null;
        }
      }
      try {
        const res = await client.getValues({
          requests: [
            {
              requestId: reqId(),
              value: { prop: propertyId, areaId },
            },
          ],
        });
        const v = res.results?.[0]?.value;
        return v ? valueFromAosp(v) : null;
      } catch (e) {
        recordError("getProperty", e, propertyId);
        return null;
      }
    },
    [client, executeShell, recordError],
  );

  // ── setProperty ──────────────────────────────────────────────────
  const setProperty = useCallback(
    async (value: VhalPropertyValue): Promise<boolean> => {
      const { propertyId, areaId = 0 } = value;

      if (transportRef.current === "adb") {
        const data = adbFormatSetData(value);
        if (data === null) {
          recordError("setProperty", "no value field populated", propertyId);
          return false;
        }

        try {
          const out = await runShell(
            executeShell,
            `cmd car_service set-property-value ${propertyId} ${areaId} ${data}`,
            5000,
            "high",
          );
          if (out.includes("successfully")) return true;
          // CarService rejects writes to properties it considers READ-only
          // with ServiceSpecificException (code 4 = ACCESS_DENIED). The
          // bundled static catalog's access metadata is incomplete for some
          // genuinely read-only AOSP properties (e.g. GEAR_SELECTION), so
          // CarService's own response — not the catalog — decides whether
          // to fall back to inject-vhal-event below.
          if (!/\(code 4\)/.test(out)) {
            recordError("setProperty", `set-property-value did not succeed: ${out.slice(0, 200)}`, propertyId);
            return false;
          }
        } catch (e) {
          recordError("setProperty", e, propertyId);
          return false;
        }

        try {
          const command =
            areaId !== 0
              ? `cmd car_service inject-vhal-event ${propertyId} ${areaId} ${data}`
              : `cmd car_service inject-vhal-event ${propertyId} ${data}`;
          const out = await runShell(executeShell, command, 5000, "high");
          const success =
            !out.toLowerCase().includes("error") &&
            !out.toLowerCase().includes("failed");
          if (!success) {
            recordError("setProperty", `inject-vhal-event did not succeed: ${out.slice(0, 200)}`, propertyId);
            return false;
          }
          shadowValues.current.set(`${propertyId}_${areaId}`, {
            propertyId,
            areaId,
            timestamp: Date.now(),
            ...(value.int32Values?.length ? { int32Values: [...value.int32Values] } : {}),
            ...(value.int64Values?.length ? { int64Values: [...value.int64Values] } : {}),
            ...(value.floatValues?.length ? { floatValues: [...value.floatValues] } : {}),
            ...(value.stringValue ? { stringValue: value.stringValue } : {}),
          });
          return true;
        } catch (e) {
          recordError("setProperty", e, propertyId);
          return false;
        }
      }

      try {
        const type = getPropertyType(propertyId);
        const reqValue: Partial<VehiclePropValue> = {
          prop: propertyId,
          areaId,
        };

        // Mirror the typing logic used for ADB set-property-value: route the
        // payload into the correct repeated field based on the declared
        // property type encoded in the property ID itself.
        if (
          type === VhalPropertyType.INT32 ||
          type === VhalPropertyType.INT32_VEC ||
          type === VhalPropertyType.BOOLEAN
        ) {
          if (!value.int32Values?.length) {
            recordError(
              "setProperty",
              "No int32 value provided",
              propertyId,
            );
            return false;
          }
          reqValue.int32Values = [...value.int32Values];
        } else if (
          type === VhalPropertyType.INT64 ||
          type === VhalPropertyType.INT64_VEC
        ) {
          if (!value.int64Values?.length) {
            recordError(
              "setProperty",
              "No int64 value provided",
              propertyId,
            );
            return false;
          }
          reqValue.int64Values = [...value.int64Values];
        } else if (
          type === VhalPropertyType.FLOAT ||
          type === VhalPropertyType.FLOAT_VEC
        ) {
          if (!value.floatValues?.length) {
            recordError(
              "setProperty",
              "No float value provided",
              propertyId,
            );
            return false;
          }
          reqValue.floatValues = [...value.floatValues];
        } else if (type === VhalPropertyType.STRING) {
          if (!value.stringValue) {
            recordError(
              "setProperty",
              "No string value provided",
              propertyId,
            );
            return false;
          }
          reqValue.stringValue = value.stringValue;
        } else if (type === VhalPropertyType.BYTES) {
          if (!value.byteValues?.length) {
            recordError(
              "setProperty",
              "No byte value provided",
              propertyId,
            );
            return false;
          }
          reqValue.byteValues = value.byteValues;
        } else {
          recordError(
            "setProperty",
            `Unsupported type: 0x${type.toString(16)}`,
            propertyId,
          );
          return false;
        }

        const res = await client.setValues({
          requests: [
            {
              requestId: reqId(),
              value: reqValue as VehiclePropValue,
            },
          ],
        });
        const status = res.results?.[0]?.status;
        // StatusCode.OK == 0.
        const ok = (status as unknown as number) === 0 || status === undefined;
        if (!ok) {
          recordError(
            "setProperty",
            `setValues status=${status}`,
            propertyId,
          );
        }
        return ok;
      } catch (e) {
        recordError("setProperty", e, propertyId);
        return false;
      }
    },
    [client, executeShell, recordError],
  );

  // ── subscribeToProperty (polling) ────────────────────────────────
  const subscribeToProperty = useCallback(
    (
      propertyId: number,
      callback: (value: VhalPropertyValue) => void,
      intervalMs = 1000,
    ): (() => void) => {
      let cancelled = false;
      const tick = async () => {
        if (cancelled) return;
        const v = await getProperty(propertyId);
        if (!cancelled && v) callback(v);
      };
      void tick();
      const interval = setInterval(() => void tick(), intervalMs);
      return () => {
        cancelled = true;
        clearInterval(interval);
      };
    },
    [getProperty],
  );

  // ── getAllPropertyValues ─────────────────────────────────────────
  // Issues one batch GetValues for every discovered (prop, areaId) pair.
  // The proxy server accepts multi-request GetValues, so this is cheap on
  // the wire even for ~500 props. We don't open a streaming subscription
  // for the inspector's snapshot — callers expect a one-shot Map.
  const getAllPropertyValues = useCallback(async (): Promise<
    Map<string, ParsedPropertyValue>
  > => {
    const result = new Map<string, ParsedPropertyValue>();
    const configs = discoveredProperties ?? (await listProperties());
    if (!configs.length) return result;

    const targets: { propertyId: number; areaId: number }[] = [];
    for (const cfg of configs) {
      // Skip WRITE-only properties — reading them returns errors.
      if (cfg.access === VhalPropertyAccess.WRITE) continue;
      const areas =
        cfg.areaConfigs && cfg.areaConfigs.length > 0
          ? cfg.areaConfigs.map((a) => a.areaId)
          : [0];
      for (const areaId of areas) {
        targets.push({ propertyId: cfg.propertyId, areaId });
      }
    }

    if (!targets.length) return result;

    if (transportRef.current === "adb") {
      // No batch RPC over the ADB-in-browser transport — read each
      // property through getProperty, which already applies the shadow
      // overlay for injected READ-only properties.
      await Promise.all(
        targets.map(async ({ propertyId, areaId }) => {
          const v = await getProperty(propertyId, areaId);
          if (v) result.set(`${propertyId}_${areaId}`, v as ParsedPropertyValue);
        }),
      );
      return result;
    }

    try {
      const res = await client.getValues({
        requests: targets.map(({ propertyId, areaId }) => ({
          requestId: reqId(),
          value: { prop: propertyId, areaId },
        })),
      });
      for (const r of res.results ?? []) {
        if (!r.value) continue;
        const parsed = parsedFromAosp(r.value);
        result.set(
          `${parsed.propertyId}_${parsed.areaId ?? 0}`,
          parsed,
        );
      }
    } catch (e) {
      recordError("getAllPropertyValues", e);
    }
    return result;
  }, [client, discoveredProperties, getProperty, listProperties, recordError]);

  // ── Service object ────────────────────────────────────────────────
  const service = useMemo<VhalService>(
    () => ({
      isAvailable,
      discoveredProperties,
      isDiscovering,
      lastError,
      clearError,
      discoverProperties,
      listProperties,
      getPropertyConfig,
      getProperty,
      setProperty,
      subscribeToProperty,
      getPropertyInfo,
      getAllPropertyValues,
      transport,
      catalogSource,
      retry,
    }),
    [
      transport,
      catalogSource,
      retry,
      isAvailable,
      discoveredProperties,
      isDiscovering,
      lastError,
      clearError,
      discoverProperties,
      listProperties,
      getPropertyConfig,
      getProperty,
      setProperty,
      subscribeToProperty,
      getAllPropertyValues,
    ],
  );

  return (
    <VhalServiceContext.Provider value={service}>
      {children}
    </VhalServiceContext.Provider>
  );
};

// ─── Unavailable singleton ────────────────────────────────────────────────
// Used when no emulator is selected. Keeps panels safe to render without
// spawning real RPCs.
const unavailableService: VhalService = {
  isAvailable: false,
  discoveredProperties: null,
  isDiscovering: false,
  lastError: null,
  clearError: () => {},
  discoverProperties: async () => [],
  listProperties: async () => [],
  getPropertyConfig: () => null,
  getProperty: async () => null,
  setProperty: async () => false,
  subscribeToProperty: () => () => {},
  getPropertyInfo,
  getAllPropertyValues: async () => new Map(),
};
